import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { FOCUSABLE } from './Modal';

// ------------------------------------------------------------------
// Anchored portal panel — popovers render into document.body so they can
// never be clipped by section cards (backdrop-filter creates a stacking
// context per card, which traps z-indexed children). Tracks the anchor on
// scroll/resize and flips upward when there is no room below.
// Closes on Esc and backdrop click; focus returns to the anchor on close,
// and (by default) moves to the panel's first focusable element on open.
// ------------------------------------------------------------------
export function AnchoredPanel({
  anchor,
  align = 'left',
  width,
  children,
  onClose,
  label,
  collisionPadding = 16,
  blank = false,
  initialFocus = true,
  role = 'dialog',
}: {
  anchor: HTMLElement;
  align?: 'left' | 'right';
  width: number;
  children: React.ReactNode;
  onClose: () => void;
  /** Accessible name for the popover region. */
  label?: string;
  /** Minimum gap kept from the viewport edge when positioning. Defaults to 16. */
  collisionPadding?: number;
  /** Skips the default background/border/shadow so the caller can fully own the visual. */
  blank?: boolean;
  /** Auto-focuses the first focusable element inside on mount. Defaults to true. */
  initialFocus?: boolean;
  /** The popover's ARIA role. Defaults to `dialog` (every pre-existing consumer).
   * `OverflowMenu` (story 2-137) passes `menu` — same portal/positioning/Esc/
   * focus-return mechanics, a different landmark role for its menuitem
   * children. */
  role?: 'dialog' | 'menu';
}): JSX.Element {
  const [rect, setRect] = useState(() => anchor.getBoundingClientRect());
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => setRect(anchor.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor]);

  useEffect(() => {
    if (!initialFocus || !panelRef.current) return;
    const first = panelRef.current.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [initialFocus]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Focus returns to the trigger when the popover unmounts, so keyboard
  // users are never dropped back to <body>.
  useEffect(() => () => anchor.focus?.(), [anchor]);

  const spaceBelow = window.innerHeight - rect.bottom;
  const openUp = spaceBelow < 320 && rect.top > 340;
  const style: CSSProperties = {
    position: 'fixed',
    width: Math.min(width, window.innerWidth - collisionPadding),
    maxHeight: openUp ? rect.top - collisionPadding : Math.max(spaceBelow - collisionPadding, 160),
    overflowY: 'auto',
    ...(openUp
      ? { bottom: window.innerHeight - rect.top + 4 }
      : { top: rect.bottom + 4 }),
    ...(align === 'right'
      ? { right: Math.max(window.innerWidth - rect.right, collisionPadding / 2) }
      : {
          left: Math.max(
            collisionPadding / 2,
            Math.min(rect.left, window.innerWidth - Math.min(width, window.innerWidth - collisionPadding) - collisionPadding / 2),
          ),
        }),
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70]" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role={role}
        aria-label={label}
        style={style}
        className={blank ? 'z-[80]' : 'glass z-[80] rounded-xl'}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
